import React, { Component } from 'react';
import _ from 'lodash';
import { Button } from './styles';
import {
  Flex, Text, Box, DisabledActionButton, ActionNote,
} from './components';
import Axios from './axios';
import { isActionAssigneeMatchCompanyStaffV2 } from '../../utils';
import ADPFormActionContent from './ADPFormActionContent';

class ADPReview extends Component {
  constructor(props) {
    super(props);
    this.employeeForm = React.createRef();
    this.hrForm = React.createRef();
    this.state = {
      isEmployeeChecked: false,
      isHRChecked: false,
      formsConfirmError: false,
    };
  }

  handleSend() {
    const allFormsConfirmed = (() => {
      const { isEmployeeChecked, isHRChecked } = this.state;
      return isEmployeeChecked && isHRChecked;
    })();

    if (!allFormsConfirmed) {
      this.setState({ formsConfirmError: true });
      return;
    }

    // append form submission if reviewer changed the questions
    const submissions = [];
    const employeeSubmission = this.employeeForm.current.handleSubmitForm({ preventDefault: () => { } });
    const hrSubmission = this.hrForm.current.handleSubmitForm({ preventDefault: () => { } });
    if (!employeeSubmission || !hrSubmission) {
      alert('Something wrong with the forms, please take a look.');
      return;
    }

    if (employeeSubmission.hasChanged) {
      submissions.push({
        id: employeeSubmission.id,
        answers: employeeSubmission.answers,
      });
    }

    if (hrSubmission.hasChanged) {
      submissions.push({
        id: hrSubmission.id,
        answers: hrSubmission.answers,
      });
    }

    const { action: { id: actionId }, companyId } = this.props;
    const config = {
      method: 'POST',
      url: APIHOST + '/v1/employee_onboarding_actions/:id/send_to_adp'.replace(':id', actionId),
      data: { ...(submissions.length > 0 && { onboarding_form_submissions: submissions }) },
      headers: {
        ...(authCode && { Authorization: authCode }),
        ...(companyId && { 'ws-company-id': companyId }),
      },
    };
    this.setState({ isSubmitting: true, saveDataError: undefined });
    Axios(config)
      .then((responseData) => {
        this.setState({ isSubmitting: false });
        if (this.props.onSendToADPSuccess) {
          this.props.onSendToADPSuccess(responseData);
        }
      })
      .catch((error) => {
        let saveDataError = 'Something went wrong while saving data.';
        if (error && error.response && error.response.data) {
          const { error_code: errorCode, message } = error.response.data;
          if (message) {
            saveDataError = message;
          }
          if (errorCode === 43001 || errorCode === 43002) {
            // Need to refresh the side bar action status
            if (this.props.refreshProgressDataFn) this.props.refreshProgressDataFn();
          }
        }
        this.setState({ isSubmitting: false, saveDataError });
      });
  }

  renderEmployeeForm() {
    const { form_submissions } = this.props.action;
    const employeeSubmission = _.find(
      form_submissions,
      (item) => item.employee_onboarding_action.action_type === 'employee_fill_adp_form',
    );
    if (!employeeSubmission || employeeSubmission.status === 'not_started') return null;
    const assignees = _.cloneDeep(this.props.action.assignees);
    assignees[0].form_submission = employeeSubmission;
    const action = {
      assignees,
      action_type: 'employee_fill_adp_form',
    };
    return (
      <Flex column>
        <ADPFormActionContent
          action={action}
          title="Employee ADP form"
          reviewActions={['edit']}
          ref={this.employeeForm}
        />
      </Flex>
    );
  }

  renderHRForm() {
    const { form_submissions } = this.props.action;
    const hrSubmission = _.find(
      form_submissions,
      (item) => item.employee_onboarding_action.action_type === 'hr_fill_adp_form' && item.status === 'submitted',
    );
    if (!hrSubmission) return null;
    const assignees = _.cloneDeep(this.props.action.assignees);
    assignees[0].form_submission = hrSubmission;
    assignees[0].is_master = true;
    const action = {
      assignees,
      action_type: 'hr_fill_adp_form',
    };
    return (
      <Flex mt={35} column>
        <ADPFormActionContent action={action} title="HR ADP form" reviewActions={['edit']} ref={this.hrForm} />
      </Flex>
    );
  }

  renderErrors() {
    const { action: { templateValid } } = this.props;
    let errorMessage = '';
    if (templateValid === false) {
      errorMessage = 'This form is outdated and cannot be submitted. Please select a new onboarding template and restart the onboarding process for this employee.';
    }
    return (
      errorMessage && (
        <Text color="red">
          {errorMessage}
        </Text>
      )
    );
  }

  renderButtons() {
    if (this.props.previewMode) {
      return <DisabledActionButton />;
    }

    let disabled = false;
    let disableNote = '';
    const {
      action: {
        assignees, action_type: actionType, status: actionStatus, templateValid,
      }, companyStaff,
    } = this.props;
    const { saveDataError } = this.state;
    if (actionStatus === 'completed') {
      return <DisabledActionButton disableNote="This step has been completed." />;
    }
    if (templateValid === false) {
      disabled = true;
      disableNote = 'This form is outdated and cannot be submitted. Please select a new onboarding template and restart the onboarding process for this employee.';
    } else if (actionStatus === 'not_started' && !isActionAssigneeMatchCompanyStaffV2(assignees, companyStaff)) {
      const assigneeNames = (() => {
        if (!assignees || !assignees.length === 0 || !assignees[0]) return null;
        return assignees.map((item) => item.company_staff && item.company_staff.name);
      })();
      disabled = true;
      disableNote = `This step should be completed by ${assigneeNames.join(' or ')}.`;
    }

    return (
      <Flex mt={20} column alignItems="flex-end">
        <Button onClick={this.handleSend.bind(this)} loading={this.state.isSubmitting} disabled={disabled}>
          Confirm and Send to ADP
        </Button>
        {disabled && <ActionNote text={disableNote} />}
        {saveDataError && <Text color="red">{saveDataError}</Text>}
      </Flex>
    );
  }

  render() {
    const { action, previewMode } = this.props;
    const employeeSubmission = _.find(
      action.form_submissions,
      (item) => item.employee_onboarding_action.action_type === 'employee_fill_adp_form',
    );
    const hrSubmission = _.find(
      action.form_submissions,
      (item) => item.employee_onboarding_action.action_type === 'hr_fill_adp_form' && item.status === 'submitted',
    );

    return (
      <Flex mb={20} flexDirection="column">
        { this.renderErrors() }
        {!previewMode && this.renderEmployeeForm()}
        {!previewMode && this.renderHRForm()}
        {!previewMode && <Box mb={20} />}
        {/* review section */}
        <Flex column bg="#F6F8F9" px="24px" py="18px" borderRadius="4px">
          <Text fontSize="22px" bold>Final review and send to ADP</Text>
          <Flex alignItems="center" mt={25}>
            <input
              disabled={
                !previewMode
                && (employeeSubmission.status === 'not_started')
}
              type="checkbox"
              checked={this.state.isEmployeeChecked}
              onChange={(e) => {
                this.setState({
                  isEmployeeChecked: e.target.checked,
                  formsConfirmError: false,
                });
              }}
            />
            <Box mr={1} />
            <Text>
              I have reviewed the employee's submission.
            </Text>
          </Flex>
          <Flex alignItems="center" mt={10}>
            <input
              disabled={!previewMode && !hrSubmission}
              type="checkbox"
              checked={this.state.isHRChecked}
              onChange={(e) => {
                this.setState({ isHRChecked: e.target.checked, formsConfirmError: false });
              }}
            />
            <Box mr={1} />
            <Text>
              I have reviewed the hiring managers's submission.
            </Text>
          </Flex>
          <Text color="red">
            {this.state.formsConfirmError && 'Please make sure you reviewed all the forms.'}
          </Text>
          {this.renderButtons()}
        </Flex>
      </Flex>
    );
  }
}

export default ADPReview;
